import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import NoIndexHelmet from '../../components/seo/NoIndexHelmet';
import { useAuth } from '../../components/context/AuthContext';
import { useNotification } from '../../components/ui/Notification';
import { db } from '../../firebase';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { collection, query, where, getDocs } from 'firebase/firestore';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { Button, Container, Row, Col, Alert, Spinner, Card } from 'react-bootstrap'; // Added Card
import { createLogger } from '../../utils/logger';

const logger = createLogger('PaymentPage');

// HTTP endpoint for employee notifications (avoids CORS issues)
const EMPLOYEE_NOTIFICATION_FUNCTION_URL = process.env.REACT_APP_EMPLOYEE_NOTIFICATION_FUNCTION_URL || 
  'https://us-central1-pack-my-cart-dev.cloudfunctions.net/notifyEmployeesNewOrderHttp';

const processPaymentAPI = (transactionId, _amount) => {
  // logger.debug(`[PaymentPage] Simulating payment for transaction ${transactionId} of amount $${amount}`);
  return new Promise((resolve) => {
    setTimeout(() => {
      const isSuccess = Math.random() > 0.1;
      if (isSuccess) {
        resolve({ success: true, paymentGatewayTransactionId: `sim_pmt_${Date.now()}_${transactionId}` });
      } else {
        resolve({ success: false, errorCode: 'SIM_PAYMENT_DECLINED', errorMessage: 'Simulated payment decline.' });
      }
    }, 2000);
  });
};

export default function PaymentPage() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [employeeCartAlert, setEmployeeCartAlert] = useState(null);
  const employeeCartTimeoutRef = useRef(null);
  const { currentUser, userRole } = useAuth();
  
  const paymentType = process.env.REACT_APP_PAYMENT_TYPE || 'wipay';
  const paymentGateway = process.env.REACT_APP_PAYMENT_GATEWAY || 'WiPay';
  const paymentMethod = process.env.REACT_APP_PAYMENT_METHOD || 'Credit Card';

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/carts", label: "Cart" },
    { label: "Payment Confirmation" }
  ];

  useEffect(() => {
    if (!currentUser) {
      setError("Please log in to proceed."); setLoading(false); return;
    }
    if (!transactionId) {
      setError("Transaction ID missing."); setLoading(false); return;
    }
    const transactionRef = db.collection('Transactions').doc(transactionId);
    const unsubscribe = transactionRef.onSnapshot(
      (docSnapshot) => {
        if (docSnapshot.exists) {
          const transData = { id: docSnapshot.id, ...docSnapshot.data() };
          const ownsTransaction =
            transData.userId === currentUser.uid || transData.cartDocId === currentUser.uid;
          if (!ownsTransaction) {
            setError("Unauthorized to view this transaction."); setTransaction(null);
          } else {
            setTransaction(transData); 
            if (['payment_successful', 'awaiting_packing', 'packed_awaiting_pickup', 'completed'].includes(transData.status)) {
                setPaymentStatus('success');
            }
          }
        } else {
          setError("Transaction not found."); setTransaction(null);
        }
        setLoading(false);
      },
      (_err) => {
        setError("Failed to load transaction."); setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [currentUser, transactionId]);

  // Notify employees when a new cart is ready to be packed
  useEffect(() => {
    if (userRole !== 'Employee') return;
    // Listen for new transactions with status 'payment_successful' or 'awaiting_packing'
    const unsubscribe = db.collection('Transactions')
      .where('status', 'in', ['payment_successful', 'awaiting_packing'])
      .orderBy('createdAt', 'desc')
      .limit(1)
      .onSnapshot(snapshot => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const data = doc.data();
          // Optionally, you can filter further by supermarketId if needed
          setEmployeeCartAlert(`New cart ready to be packed for ${data.supermarketName || 'a supermarket'}!`);
          if (employeeCartTimeoutRef.current) clearTimeout(employeeCartTimeoutRef.current);
          employeeCartTimeoutRef.current = setTimeout(() => setEmployeeCartAlert(null), 5000);
        }
      });
    return () => {
      unsubscribe();
      if (employeeCartTimeoutRef.current) clearTimeout(employeeCartTimeoutRef.current);
    };
  }, [userRole]);

  const handleConfirmPayment = async () => {
    if (!transaction || transaction.confirmationFee <= 0 || (transaction.status !== 'initiated' && transaction.status !== 'payment_failed')) {
      showNotification({ type: 'error', title: 'Payment Error', message: 'Cannot process payment at this time.' });
      return;
    }
    setPaymentStatus('processing'); setPaymentError(null);
    const paymentResult = await processPaymentAPI(transaction.id, transaction.confirmationFee);
    const transactionDocRef = db.collection('Transactions').doc(transaction.id);
    const userCartRef = db.collection('Carts').doc(currentUser.uid);
    try {
      if (paymentResult.success) {
        const updateData = {
          status: 'payment_successful', 
          amountPaid: transaction.confirmationFee,
          paymentGatewayTransactionId: paymentResult.paymentGatewayTransactionId,
          paymentGateway: paymentType === 'simulated' ? 'Simulated' : paymentGateway,
          paymentMethod: paymentType === 'simulated' ? 'Simulated Payment' : paymentMethod,
          message: 'Payment processed successfully',
          total: transaction.confirmationFee,
          orderSubtotal: transaction.orderSubtotal || transaction.total || transaction.confirmationFee,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        };
        
                  // logger.debug('Updating transaction with data:', updateData);
        await transactionDocRef.update(updateData);
        if (transaction.supermarketId && transaction.cartInstanceId) {
          const cartInstanceUpdate = {};
          const cartInstancePathPrefix = `${transaction.supermarketId}.${transaction.cartInstanceId}`;
          cartInstanceUpdate[`${cartInstancePathPrefix}.status`] = 'awaiting_packing';
          cartInstanceUpdate[`${cartInstancePathPrefix}.transactionId`] = transaction.id;
          cartInstanceUpdate[`${cartInstancePathPrefix}.lastUpdatedAt`] = firebase.firestore.FieldValue.serverTimestamp();
          cartInstanceUpdate.lastGlobalUpdate = firebase.firestore.FieldValue.serverTimestamp();
          try { await userCartRef.update(cartInstanceUpdate); }
          catch (cartUpdateError) { logger.error(`[PaymentPage] Cart status update failed:`, cartUpdateError); }
        }
        setPaymentStatus('success');
        
        // Notify employees about new order awaiting packing
        const enableEmployeeNotifications = process.env.REACT_APP_ENABLE_EMPLOYEE_NOTIFICATIONS === 'true' || 
                                           process.env.REACT_APP_ENABLE_EMPLOYEE_NOTIFICATIONS === '1';
        
        logger.debug('[PaymentPage] Employee notification check:', {
          enableEmployeeNotifications,
          hasSupermarketId: !!transaction.supermarketId,
          supermarketId: transaction.supermarketId,
          cartInstanceId: transaction.cartInstanceId,
          transactionId: transaction.id
        });
        
        if (enableEmployeeNotifications && transaction.supermarketId) {
          try {
            logger.debug('[PaymentPage] Notifying employees about new order awaiting packing');
            logger.info('[PaymentPage] Calling employee notification function:', EMPLOYEE_NOTIFICATION_FUNCTION_URL);
            
            // Fetch user's actual name instead of using userId
            let customerName = 'Customer';
            try {
              const userQuery = query(collection(db, 'Users'), where('identity_id', '==', transaction.userId));
              const userSnapshot = await getDocs(userQuery);
              if (!userSnapshot.empty) {
                const userData = userSnapshot.docs[0].data();
                customerName = userData.first_name || userData.displayName || 'Customer';
              }
            } catch (userError) {
              logger.warn('[PaymentPage] Failed to fetch user name for notification:', userError);
            }
            
            // Use HTTP endpoint to avoid CORS issues
            // Don't block on notification - fire and forget to prevent UI delays
            const notificationPayload = {
              orderId: transaction.cartInstanceId,
              transactionId: transaction.id,
              customerName: customerName,
              supermarketId: transaction.supermarketId
            };
            
            logger.info('[PaymentPage] Sending employee notification request:', {
              url: EMPLOYEE_NOTIFICATION_FUNCTION_URL,
              payload: notificationPayload
            });
            
            fetch(EMPLOYEE_NOTIFICATION_FUNCTION_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(notificationPayload)
            })
            .then(response => {
              logger.info('[PaymentPage] Employee notification response status:', response.status, response.statusText);
              if (!response.ok) {
                logger.warn('[PaymentPage] Employee notification failed:', response.status);
                logger.error('[PaymentPage] Employee notification HTTP error:', response.status, response.statusText);
              } else {
                return response.json();
              }
            })
            .then(result => {
              logger.info('[PaymentPage] Employee notification result:', result);
              if (result && result.success) {
                logger.debug('[PaymentPage] Employee notification sent successfully');
                logger.info('[PaymentPage] ✅ Employee notification sent successfully:', result);
              } else if (result) {
                logger.warn('[PaymentPage] Employee notification returned success: false', result);
                logger.warn('[PaymentPage] ⚠️ Employee notification returned success: false', result);
              }
            })
            .catch(notificationError => {
              logger.warn('[PaymentPage] Failed to notify employees:', notificationError);
              logger.error('[PaymentPage] ❌ Failed to notify employees:', notificationError);
              // Don't fail the payment process if notification fails
            });
            // Continue immediately without waiting for notification
          } catch (notificationError) {
            logger.warn('[PaymentPage] Failed to notify employees:', notificationError);
            logger.error('[PaymentPage] ❌ Exception in employee notification:', notificationError);
            // Don't fail the payment process if notification fails
          }
        } else {
          logger.debug('[PaymentPage] Employee notification skipped:', {
            enableEmployeeNotifications,
            hasSupermarketId: !!transaction.supermarketId
          });
          logger.info('[PaymentPage] ⏭️ Employee notification skipped - conditions not met:', {
            enableEmployeeNotifications,
            hasSupermarketId: !!transaction.supermarketId
          });
        }
        
        // Force refresh of transaction data to show updated values
        setTimeout(async () => {
          try {
            const transactionRef = db.collection('Transactions').doc(transaction.id);
            const docSnapshot = await transactionRef.get();
            if (docSnapshot.exists) {
              const updatedTransaction = { id: docSnapshot.id, ...docSnapshot.data() };
              // logger.debug('Updated transaction data after payment:', updatedTransaction);
              setTransaction(updatedTransaction);
            }
          } catch (error) {
            logger.error('Error refreshing transaction data:', error);
          }
        }, 1000);
      } else {
        await transactionDocRef.update({
          status: 'payment_failed', paymentErrorCode: paymentResult.errorCode,
          paymentErrorMessage: paymentResult.errorMessage, updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        setPaymentStatus('failed');
        setPaymentError(paymentResult.errorMessage || 'Payment failed.');
      }
        } catch (err) {
      setPaymentStatus('failed');
      setPaymentError(`Error updating records: ${  err.message}`);
    }
  };

  if (loading) return <Layout><Container className="mt-4 text-center"><Spinner animation="border" /> <p>Loading payment details...</p></Container></Layout>;
  
  const isPaymentProcessed = paymentStatus === 'success' || 
                           (transaction && ['payment_successful', 'awaiting_packing', 'packed_awaiting_pickup', 'completed'].includes(transaction.status));

  return (
    <Layout>
      <NoIndexHelmet title="Payment | Pack My Cart" path="/payment" />
      <Container className="mt-4">
        {/* Employee notification alert */}
        {employeeCartAlert && (
          <Alert variant="info" className="mb-3 text-center" style={{position: 'sticky', top: 0, zIndex: 1050}}>
            {employeeCartAlert}
          </Alert>
        )}

        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={() => navigate(isPaymentProcessed ? `/order-status/${transactionId}` : '/carts')} className="mb-2">
                    &larr; {isPaymentProcessed ? "View Order Status" : "Back to Carts"}
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>

        {error && <Alert variant="danger" className="mt-3">{error} <Link to="/">Go Home</Link></Alert>}
        {!error && !transaction && !loading && <Alert variant="warning" className="mt-3">Could not load transaction details.</Alert>}

        {transaction && isPaymentProcessed && (
            <div className="text-center mt-3">
                <h3>Payment Processed Successfully!</h3>
                {error && <Alert variant="warning" className="mt-2">Notice: {error}</Alert>}
                
                <Card className="mt-3 mb-3">
                    <Card.Body>
                        <h5 className="text-success mb-3">Payment Details</h5>
                        {transaction.paymentType === 'full' && (
                          <div className="alert alert-success mb-3 text-center">
                            <strong><i className="fas fa-check-circle me-2"></i>FULL PAYMENT ORDER</strong>
                            <br />
                            <small>The full order total has been paid. Confirmation fee collected as processing fee.</small>
                          </div>
                        )}
                        <div className="row text-start">
                            <div className="col-md-6">
                                <p><strong>Order ID:</strong> {transaction.cartInstanceId ? transaction.cartInstanceId.replace('cart_instance_', '') : transaction.id}</p>
                                <p><strong>Status:</strong> <span className="badge bg-success">{transaction.status?.replace(/_/g,' ').replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase())) || 'N/A'}</span></p>
                                <p><strong>Payment Type:</strong> {transaction.paymentType === 'full' ? (
                                  <span className="badge bg-success"><i className="fas fa-check-circle me-1"></i>Full Payment</span>
                                ) : (
                                  <span className="badge bg-secondary">Confirmation Fee Only</span>
                                )}</p>
                                <p><strong>Message:</strong> {transaction.message || 'Payment completed successfully'}</p>
                                <p><strong>Transaction ID:</strong> {transaction.paymentGatewayTransactionId || transaction.id || 'N/A'}</p>
                                {transaction.paymentGatewayTransactionId && transaction.paymentGatewayTransactionId !== transaction.id && (
                                  <p><strong>Document ID:</strong> {transaction.id}</p>
                                )}
                                <p><strong>Total Paid:</strong> <span className="fs-5 fw-bold text-success">${(transaction.amountPaid || transaction.confirmationFee || transaction.total || transaction.orderSubtotal || 0).toFixed(2)}</span></p>
                            </div>
                            <div className="col-md-6">
                                <p><strong>Supermarket:</strong> {transaction.supermarketName || 'N/A'}</p>
                                <p><strong>Order Subtotal:</strong> ${transaction.orderSubtotal?.toFixed(2) || 'N/A'} {transaction.paymentType === 'full' ? (
                                  <span className="text-success">(Paid)</span>
                                ) : (
                                  <span>(Due at pickup)</span>
                                )}</p>
                                <p><strong>{transaction.paymentType === 'full' ? 'Processing Fee (Confirmation Fee):' : 'Confirmation Fee:'}</strong> ${transaction.confirmationFee?.toFixed(2) || 'N/A'} {transaction.paymentType === 'full' && (
                                  <span className="text-muted">(Included in total)</span>
                                )}</p>
                                {transaction.paymentType === 'full' && (
                                  <p><strong className="text-success">Full Amount Paid:</strong> <span className="fs-5 fw-bold text-success">${((transaction.orderSubtotal || 0) + (transaction.confirmationFee || 0) + (Number(transaction.deliveryFee) || 0)).toFixed(2)}</span></p>
                                )}
                                <p><strong>Payment Gateway:</strong> {transaction.paymentGateway || 'N/A'}</p>
                                <p><strong>Payment Method:</strong> {transaction.paymentMethod || 'N/A'}</p>
                                {transaction.deliveryOption === 'delivery' && (
                                    <>
                                        <p><strong>Delivery Option:</strong> <span className="badge bg-info">Delivery</span></p>
                                        {transaction.deliveryAddress && (
                                            <p><strong>Delivery Address:</strong> {transaction.deliveryAddress}</p>
                                        )}
                                        {transaction.deliveryPhone && (
                                            <p><strong>Delivery Phone:</strong> {transaction.deliveryPhone}</p>
                                        )}
                                        {(Number(transaction.deliveryFee) || 0) > 0 && (
                                            <p><strong>Delivery Fee:</strong> ${(Number(transaction.deliveryFee) || 0).toFixed(2)}</p>
                                        )}
                                        {transaction.maxDeliveryDistance && (
                                            <p><strong>Max Delivery Distance:</strong> {transaction.maxDeliveryDistance} km</p>
                                        )}
                                    </>
                                )}
                                {transaction.deliveryOption === 'pickup' && (
                                    <p><strong>Delivery Option:</strong> <span className="badge bg-secondary">Pickup</span></p>
                                )}
                                {!transaction.deliveryOption && (
                                    <p><strong>Delivery Option:</strong> <span className="badge bg-secondary">Pickup (Default)</span></p>
                                )}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                
                <div className="mt-3">
                    <Link to={`/order-status/${transaction.id}`} className="btn btn-primary me-2">View Full Order Status</Link>
                    <Link to="/carts" className="btn btn-info me-2">Back to Carts</Link>
                    <Link to="/" className="btn btn-outline-secondary">Return Home</Link>
                </div>
            </div>
        )}

        {transaction && !isPaymentProcessed && (transaction.status === 'initiated' || transaction.status === 'payment_failed') && (
          <div className="mt-3">
            <h2 className="mb-3">Pay Confirmation Fee</h2>
            <Card>
                <Card.Body>
                    <p><strong>Order from:</strong> {transaction.supermarketName || 'Supermarket'}</p>
                    <p><strong>Cart ID:</strong> {transaction.cartInstanceId ? transaction.cartInstanceId.replace('cart_instance_','') : 'N/A'}</p>
                    <p><strong>Fee to Pay:</strong> ${transaction.confirmationFee?.toFixed(2) || '0.00'}</p>
                    <p className="text-muted small">Subtotal ${transaction.orderSubtotal?.toFixed(2) || '0.00'} to be paid at pickup.</p>
                    {paymentStatus === 'processing' && <Alert variant="info" className="mt-3">Processing payment...</Alert>}
                    {paymentStatus === 'failed' && paymentError && <Alert variant="danger" className="mt-3">Payment Failed: {paymentError}</Alert>}
                    <Button className="btn btn-success btn-lg w-100 mt-3" onClick={handleConfirmPayment} disabled={paymentStatus === 'processing'}>
                        {paymentStatus === 'processing' ? <><Spinner as="span" size="sm" /> Processing...</> : `Confirm Payment of $${transaction.confirmationFee?.toFixed(2) || '0.00'}`}
                    </Button>
                    {transaction.status === 'payment_failed' && paymentStatus !== 'processing' && <p className="mt-2 text-info text-center">You can try paying again.</p>}
                </Card.Body>
            </Card>
          </div>
        )}
      </Container>
    </Layout>
  );
}
